import { useCallback, useContext, useEffect, useState, useRef } from "react"
import { Button, Col, Collapse, Input, message, Popconfirm, Row } from "antd"
import { useNavigate } from "react-router-dom"
import { askVertex } from "../services/VertexServices"
import { RequestSection } from "./RequestSection"
import { RequestSectionEcograph } from "./RequestSectionEcograph"
import { ObservationsSection } from "./ObservationsSection"
import { ObservationsSectionEcograph } from "./ObservationsSectionEcograph"
import { fixEncodingIssues } from "../utils/encoding/encoding"
import { useAuth } from "../contexts/authContext"
import InformeModal from "./InformeModal"
import { ImageContext } from "../contexts/imageContext/imageContext"
import { saveReportDetails, updateImages } from "../services/ReportService"
import { useTranslation } from "react-i18next"
import MicrophoneButton from "./MicrophoneButton"
import { formatDiagnosis } from "../utils/diagnosis/formatDiagnosis"

const { Panel } = Collapse
const { TextArea } = Input

/**
 * Component for managing medical reports in the right column of the application.
 * Handles request, observations, and diagnosis sections.
 *
 * @param {Object} props - Component props
 * @param {number} props.regenerateCount - The number of times diagnosis has been regenerated
 * @param {Function} props.setRegenerateCount - Function to update the regenerate count
 * @param {Function} props.setIsModalVisible - Function to control modal visibility from parent
 * @param {string|number} props.width - The width of the column
 * @param {boolean} props.isCentralColumnCollapsed - Whether the central column is collapsed
 * @param {boolean} props.isLargeScreen - Whether the screen is large
 * @param {string} props.reportType - The type of report ('radiograph' or 'ecograph')
 * @returns {JSX.Element} The RightColumn component
 */
export const RightColumn = ({
  regenerateCount,
  setRegenerateCount,
  setIsModalVisible,
  width,
  isCentralColumnCollapsed,
  isLargeScreen,
  reportType = "radiograph",
}) => {
  const { t } = useTranslation()
  const [selectedStructure, setSelectedStructure] = useState("")
  const [selectedSubstructure, setSelectedSubstructure] = useState("")
  const [selectedIncidences, setSelectedIncidences] = useState([])
  const [selectedSystem, setSelectedSystem] = useState("")
  const [selectedStructure2, setSelectedStructure2] = useState("")
  const [selectedSubstructure2, setSelectedSubstructure2] = useState("")
  const [selectedSubsubestructura, setSelectedSubsubestructura] = useState("")
  const [selectedSubsubsubestructura, setSelectedSubsubsubestructura] = useState("")
  const [problem, setProblem] = useState("")
  const [problemDescription, setProblemDescription] = useState("")
  const [isObservationsEnabled, setIsObservationsEnabled] = useState(false)
  const [observations, setObservations] = useState([])
  const [isGenerating, setIsGenerating] = useState(false)
  const [isReadyToGenerate, setIsReadyToGenerate] = useState(false)
  const [isGenerated, setIsGenerated] = useState(false)
  const [redaccion, setRedaccion] = useState("")
  const [alreadyGenerated, setAlreadyGenerated] = useState(false)
  const [isModalVisible, setIsModalVisibleInternal] = useState(false)
  const [vertexResponse, setVertexResponse] = useState({})
  const [isSaving, setIsSaving] = useState(false)

  // Voice dictation states for diagnosis
  const [currentDiagnosisTranscript, setCurrentDiagnosisTranscript] = useState("")
  const [isDiagnosisMicrophoneActive, setIsDiagnosisMicrophoneActive] = useState(false)
  const isDiagnosisTranscribingRef = useRef(false)

  const navigate = useNavigate()

  const [isLoaded, setIsLoaded] = useState(false)

  const { getToken } = useAuth()

  const { clearImages } = useContext(ImageContext)

  // Determine which components to use based on report type
  const RequestSectionComponent = reportType === "ecograph" ? RequestSectionEcograph : RequestSection
  const ObservationsSectionComponent = reportType === "ecograph" ? ObservationsSectionEcograph : ObservationsSection

  /**
   * Loads saved form data from session storage.
   */
  const loadDataFromLocalStorage = useCallback(() => {
    const savedData = JSON.parse(sessionStorage.getItem("formData2") || "{}")
    if (Object.keys(savedData).length > 0) {
      setSelectedStructure(savedData.selectedStructure || "")
      setSelectedSubstructure(savedData.selectedSubstructure || "")
      setSelectedIncidences(savedData.selectedIncidences || [])
      setSelectedSystem(savedData.selectedSystem || "")
      setSelectedStructure2(savedData.selectedStructure2 || "")
      setSelectedSubstructure2(savedData.selectedSubstructure2 || "")
      setSelectedSubsubestructura(savedData.selectedSubsubestructura || "")
      setSelectedSubsubsubestructura(savedData.selectedSubsubsubestructura || "")
      setProblem(savedData.problem || "")
      setProblemDescription(savedData.problemDescription || "")
      setObservations(savedData.observations || [])
      setRedaccion(savedData.redaccion || "")
    }
    const vertexResponseData = JSON.parse(sessionStorage.getItem("vertexResponse") || "{}")
    if (Object.keys(vertexResponseData).length > 0) {
      setVertexResponse(vertexResponseData)
      setAlreadyGenerated(true)
      setIsGenerated(true)
    }
    setIsLoaded(true)
  }, [])

  useEffect(() => {
    if (!isLoaded) {
      loadDataFromLocalStorage()
    }
  }, [loadDataFromLocalStorage, isLoaded])

  useEffect(() => {
    if (isLoaded) {
      const formData2 = {
        selectedStructure,
        selectedSubstructure,
        selectedIncidences,
        selectedSystem,
        selectedStructure2,
        selectedSubstructure2,
        selectedSubsubestructura,
        selectedSubsubsubestructura,
        problem,
        problemDescription,
        observations,
        redaccion,
      }
      const currentFormData = JSON.parse(sessionStorage.getItem("formData2") || "{}")
      if (JSON.stringify(formData2) !== JSON.stringify(currentFormData)) {
        sessionStorage.setItem("formData2", JSON.stringify(formData2))
      }
    }
  }, [
    isLoaded,
    selectedStructure,
    selectedSubstructure,
    selectedIncidences,
    selectedSystem,
    selectedStructure2,
    selectedSubstructure2,
    selectedSubsubestructura,
    selectedSubsubsubestructura,
    problem,
    problemDescription,
    observations,
    redaccion,
  ])

  useEffect(() => {
    if (isLoaded && Object.keys(vertexResponse).length > 0) {
      sessionStorage.setItem("vertexResponse", JSON.stringify(vertexResponse))
    }
  }, [isLoaded, vertexResponse])

  useEffect(() => {
    if (redaccion.trim() !== "" && observations.length > 0 && observations[0].trim() !== "") {
      setIsReadyToGenerate(true)
    } else {
      setIsReadyToGenerate(false)
    }
  }, [redaccion, observations])

  useEffect(() => {
    if (redaccion.length > 0) {
      setIsObservationsEnabled(true)
    } else {
      setIsObservationsEnabled(true)
    }
  }, [redaccion])

  /**
   * Handles updates to the diagnosis transcript.
   *
   * @param {string} transcript - The current transcript from speech recognition
   */
  const handleDiagnosisTranscriptUpdate = useCallback((transcript) => {
    setCurrentDiagnosisTranscript(transcript)
    isDiagnosisTranscribingRef.current = true
  }, [])

  /**
   * Handles the completion of a diagnosis transcript.
   *
   * @param {string} finalTranscript - The final transcript from speech recognition
   */
  const handleDiagnosisTranscriptComplete = useCallback(
    (finalTranscript) => {
      setVertexResponse((prevState) => {
        const currentDiagnosis = prevState.finalDiagnosis || ""
        let newDiagnosis = currentDiagnosis

        if (isDiagnosisTranscribingRef.current) {
          if (currentDiagnosis.endsWith(currentDiagnosisTranscript)) {
            newDiagnosis =
              currentDiagnosis.substring(0, currentDiagnosis.length - currentDiagnosisTranscript.length) +
              finalTranscript
          } else {
            newDiagnosis = currentDiagnosis + (currentDiagnosis ? "\n" : "") + finalTranscript
          }
        }

        return {
          ...prevState,
          finalDiagnosis: newDiagnosis,
          modified: true,
        }
      })

      setCurrentDiagnosisTranscript("")
      isDiagnosisTranscribingRef.current = false
    },
    [currentDiagnosisTranscript],
  )

  /**
   * Saves the current report details to the server.
   */
  const handleSave = async () => {
    try {
      setIsSaving(true)

      const token = await getToken()
      if (!token) {
        console.error("No token found, unable to save report details.")
        return
      }

      const reportData = sessionStorage.getItem("report")

      if (!reportData) {
        console.error("No se encontró información del reporte en sessionStorage.")
        return
      }

      const report = JSON.parse(reportData)
      if (!report.id) {
        console.error("El ID del reporte no es válido.")
        return
      }

      await saveReportDetails(token, report.id)
      await updateImages(token, report.id)
      message.success(t("rightColumn.saveSuccess"))
    } catch (error) {
      console.error("Error al guardar los detalles del reporte:", error)
      message.error(t("rightColumn.saveError"))
    } finally {
      setIsSaving(false)
    }
  }

  /**
   * Regenerates the diagnosis using AI.
   */
  const regenerateDiagnosis = async () => {
    if (regenerateCount >= 3) return
    setIsGenerating(true)
    setRegenerateCount(regenerateCount + 1)

    const formData2 = JSON.parse(sessionStorage.getItem("formData2") || "{}")
    const formValues = JSON.parse(sessionStorage.getItem("formValues") || "{}")

    const observations = formData2.observations || []
    const prompt = `
    paciente: ${formValues.nombreAnimal || "Indefinido"}. Especie: ${formValues.especie || "Indefinida"}. Propietario: ${formValues.tutor || "Indefinido"}. Edad: ${formValues.edad || "Indefinida"} ${formValues.edadUnidad || ""}. Raza: ${formValues.raza || "Indefinida"}. Sexo: ${formValues.sexo || "Indefinido"}. Solicitud: ${formData2.redaccion || "Indefinida"} observaciones: ${observations.join(" ")}
  `.trim()
    try {
      const token = await getToken()
      if (!token) {
        console.error("No token found, unable to generate diagnosis.")
        return
      }
      const response = await askVertex(prompt, token)
      const responseText = response.generatedDiagnosis
      const fixedResponse = fixEncodingIssues(responseText.trim())
      const processedResponse = formatDiagnosis(fixedResponse)

      setVertexResponse({
        aiGenerated: response.aiGenerated,
        prompt: response.prompt,
        generatedDiagnosis: response.generatedDiagnosis,
        finalDiagnosis: processedResponse,
        modified: response.modified,
        model: response.model,
      })

      setIsGenerating(false)
      setIsGenerated(true)
      setAlreadyGenerated(true)
    } catch (error) {
      console.error("Error generating diagnosis:", error)
      setIsGenerating(false)
    }
  }

  /**
   * Clears all report data and navigates to the dashboard.
   */
  const nuevoInforme = () => {
    sessionStorage.removeItem("formData2")
    sessionStorage.removeItem("formValues")
    sessionStorage.removeItem("vertexResponse")
    sessionStorage.removeItem("regenerateCount")
    sessionStorage.removeItem("diagnostico")
    sessionStorage.removeItem("observaciones")
    sessionStorage.removeItem("observationsForm")
    sessionStorage.removeItem("solicitud")
    sessionStorage.removeItem("report")
    clearImages()
    setAlreadyGenerated(false)
    setIsGenerated(false)
    setVertexResponse({})
    navigate("/dashboard")
  }

  /**
   * Toggles the visibility of the report preview modal.
   */
  const toggleModal = () => {
    setIsModalVisibleInternal(true)
    setIsModalVisible(true)
  }

  /**
   * Updates the state based on changes made in the modal.
   *
   * @param {string} field - The field to update
   * @param {any} value - The new value
   */
  const updateStateFromModal = (field, value) => {
    switch (field) {
      case "solicitud":
        if (value !== redaccion) setRedaccion(value)
        break
      case "observaciones":
        if (JSON.stringify(value) !== JSON.stringify(observations)) setObservations(value)
        break
      case "diagnostico":
        if (value !== vertexResponse.finalDiagnosis) {
          setVertexResponse((prevState) => ({
            ...prevState,
            finalDiagnosis: value,
            modified: true,
          }))
        }
        break
      default:
        console.warn(`Unhandled field in updateStateFromModal: ${field}`)
    }
  }

  return (
    <Col className="columna-der" style={{ width: width }}>
      {isCentralColumnCollapsed && isLargeScreen ? (
        <Row>
          <Col span={12}>
            <Row justify={"center"} className="center">
              <Col span={20}>
                <Row className="input-row">
                  <Collapse defaultActiveKey={["1"]} expandIconPosition="end" className="collapse">
                    <Panel header={<span className="generating">{t("rightColumn.request")}</span>} key="1">
                      <div style={{ marginTop: "16px" }}>
                        <RequestSectionComponent
                          selectedStructure={selectedStructure}
                          setSelectedStructure={setSelectedStructure}
                          selectedSubstructure={selectedSubstructure}
                          setSelectedSubstructure={setSelectedSubstructure}
                          selectedIncidences={selectedIncidences}
                          setSelectedIncidences={setSelectedIncidences}
                          redaccion={redaccion}
                          setRedaccion={setRedaccion}
                          isLoaded={isLoaded}
                        />
                      </div>
                    </Panel>
                  </Collapse>
                </Row>
                <hr />
                <Row className="input-row">
                  <Collapse defaultActiveKey={["1"]} expandIconPosition="end">
                    <Panel
                      header={<span className="generating">{t("rightColumn.observations")}</span>}
                      key="1"
                      collapsible={isObservationsEnabled ? "header" : "disabled"}
                    >
                      <div style={{ marginTop: "16px" }}>
                        <ObservationsSectionComponent observations={observations} setObservations={setObservations} />
                      </div>
                    </Panel>
                  </Collapse>
                </Row>
              </Col>
            </Row>
          </Col>
          <Col span={12}>
            <Row justify={"center"} className="center">
              <Col span={20}>
                <hr />
                <Row className="input-row">
                  <div
                    style={{ display: "flex", justifyContent: "space-between", alignItems: "center", width: "100%" }}
                  >
                    <span className="generating diagnostico">
                      {isGenerating
                        ? t("rightColumn.diagnosisGenerating")
                        : isGenerated
                          ? t("rightColumn.diagnosisGenerated")
                          : t("rightColumn.diagnosis")}
                    </span>
                    {!isGenerating && !alreadyGenerated && (
                      <Button
                        className="generate-button vistaprevia-button"
                        onClick={regenerateDiagnosis}
                        disabled={!isReadyToGenerate}
                      >
                        {t("rightColumn.generate")}
                      </Button>
                    )}
                    {isGenerating && (
                      <div className="dots" style={{ marginLeft: "10px" }}>
                        <span style={{ "--i": 1 }}>.</span>
                        <span style={{ "--i": 2 }}>.</span>
                        <span style={{ "--i": 3 }}>.</span>
                      </div>
                    )}
                  </div>

                  {isGenerating ? (
                    <div className="skeleton-loader"></div>
                  ) : (
                    <div style={{ position: "relative", width: "100%" }}>
                      <TextArea
                        rows={6}
                        value={
                          (vertexResponse.finalDiagnosis || "") +
                          (currentDiagnosisTranscript ? `\n${currentDiagnosisTranscript}` : "")
                        }
                        onChange={(e) => {
                          setCurrentDiagnosisTranscript("")
                          const newValue = e.target.value
                          setVertexResponse((prevState) => ({
                            ...prevState,
                            finalDiagnosis: newValue,
                            modified: true,
                          }))
                        }}
                        onBlur={(e) => {
                          const newValue = e.target.value
                          const processedValue = formatDiagnosis(newValue)
                          setVertexResponse((prevState) => ({
                            ...prevState,
                            finalDiagnosis: processedValue,
                            modified: true,
                          }))
                        }}
                        placeholder={t("rightColumn.diagnosisPlaceholder")}
                        style={{ paddingRight: "30px" }}
                        className="custom-textarea"
                      />
                      <MicrophoneButton
                        onTranscriptUpdate={handleDiagnosisTranscriptUpdate}
                        onTranscriptComplete={handleDiagnosisTranscriptComplete}
                        isActive={isDiagnosisMicrophoneActive}
                        setIsActive={setIsDiagnosisMicrophoneActive}
                      />
                    </div>
                  )}

                  {(alreadyGenerated || isGenerated) && (
                    <Button
                      className="regenerate-button redesigned-secondary-button"
                      onClick={regenerateDiagnosis}
                      disabled={isGenerating || regenerateCount >= 3 || !isReadyToGenerate}
                    >
                      {t("rightColumn.regenerate")} ({3 - regenerateCount} {t("rightColumn.remaining")})
                    </Button>
                  )}
                </Row>
                <Row className="center">
                  <Button className="generate-button2 guardar-button" onClick={handleSave} loading={isSaving}>
                    {t("rightColumn.saveProcess")}
                  </Button>
                </Row>
                <Row className="center">
                  <Button
                    className="generate-button2 vistaprevia-button"
                    onClick={toggleModal}
                    disabled={!vertexResponse.finalDiagnosis || !isReadyToGenerate}
                  >
                    {t("rightColumn.previewReport")}
                  </Button>
                </Row>
                <Row className="center separate">
                  <Button className="button4 redesigned-danger-button" onClick={() => navigate(-1)}>
                    {t("rightColumn.back")}
                  </Button>
                  <Popconfirm
                    title={t("rightColumn.confirmNewReport")}
                    description={t("rightColumn.loseData")}
                    okText={t("rightColumn.yesContinue")}
                    cancelText={t("rightColumn.cancel")}
                    onConfirm={nuevoInforme}
                    okButtonProps={{ danger: true }}
                  >
                    <Button className="button5 redesigned-secondary-button">{t("rightColumn.newReport")}</Button>
                  </Popconfirm>
                </Row>
              </Col>
            </Row>
          </Col>
        </Row>
      ) : (
        <Row justify={"center"} className="center">
          <Col span={20}>
            <Row className="input-row">
              <Collapse defaultActiveKey={["1"]} expandIconPosition="end" className="collapse">
                <Panel header={<span className="generating">{t("rightColumn.request")}</span>} key="1">
                  <div style={{ marginTop: "16px" }}>
                    <RequestSectionComponent
                      selectedStructure={selectedStructure}
                      setSelectedStructure={setSelectedStructure}
                      selectedSubstructure={selectedSubstructure}
                      setSelectedSubstructure={setSelectedSubstructure}
                      selectedIncidences={selectedIncidences}
                      setSelectedIncidences={setSelectedIncidences}
                      redaccion={redaccion}
                      setRedaccion={setRedaccion}
                      isLoaded={isLoaded}
                    />
                  </div>
                </Panel>
              </Collapse>
            </Row>
            <hr />
            <Row className="input-row">
              <Collapse defaultActiveKey={["1"]} expandIconPosition="end">
                <Panel
                  header={<span className="generating">{t("rightColumn.observations")}</span>}
                  key="1"
                  collapsible={isObservationsEnabled ? "header" : "disabled"}
                >
                  <div style={{ marginTop: "16px" }}>
                    <ObservationsSectionComponent observations={observations} setObservations={setObservations} />
                  </div>
                </Panel>
              </Collapse>
            </Row>
            <hr />
            <Row className="input-row">
              <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", width: "100%" }}>
                <span className="generating diagnostico">
                  {isGenerating
                    ? t("rightColumn.diagnosisGenerating")
                    : isGenerated
                      ? t("rightColumn.diagnosisGenerated")
                      : t("rightColumn.diagnosis")}
                </span>
                {!isGenerating && !alreadyGenerated && (
                  <Button
                    className="generate-button vistaprevia-button"
                    onClick={regenerateDiagnosis}
                    disabled={!isReadyToGenerate}
                  >
                    {t("rightColumn.generate")}
                  </Button>
                )}
                {isGenerating && (
                  <div className="dots" style={{ marginLeft: "10px" }}>
                    <span style={{ "--i": 1 }}>.</span>
                    <span style={{ "--i": 2 }}>.</span>
                    <span style={{ "--i": 3 }}>.</span>
                  </div>
                )}
              </div>

              {isGenerating ? (
                <div className="skeleton-loader"></div>
              ) : (
                <div style={{ position: "relative", width: "100%" }}>
                  <TextArea
                    rows={6}
                    value={
                      (vertexResponse.finalDiagnosis || "") +
                      (currentDiagnosisTranscript ? `\n${currentDiagnosisTranscript}` : "")
                    }
                    onChange={(e) => {
                      setCurrentDiagnosisTranscript("")
                      const newValue = e.target.value
                      setVertexResponse((prevState) => ({
                        ...prevState,
                        finalDiagnosis: newValue,
                        modified: true,
                      }))
                    }}
                    onBlur={(e) => {
                      const newValue = e.target.value
                      const processedValue = formatDiagnosis(newValue)
                      setVertexResponse((prevState) => ({
                        ...prevState,
                        finalDiagnosis: processedValue,
                        modified: true,
                      }))
                    }}
                    placeholder={t("rightColumn.diagnosisPlaceholder")}
                    style={{ paddingRight: "30px" }}
                    className="custom-textarea"
                  />
                  <MicrophoneButton
                    onTranscriptUpdate={handleDiagnosisTranscriptUpdate}
                    onTranscriptComplete={handleDiagnosisTranscriptComplete}
                    isActive={isDiagnosisMicrophoneActive}
                    setIsActive={setIsDiagnosisMicrophoneActive}
                  />
                </div>
              )}

              {(alreadyGenerated || isGenerated) && (
                <Button
                  className="regenerate-button redesigned-secondary-button"
                  onClick={regenerateDiagnosis}
                  disabled={isGenerating || regenerateCount >= 3 || !isReadyToGenerate}
                >
                  {t("rightColumn.regenerate")} ({3 - regenerateCount} {t("rightColumn.remaining")})
                </Button>
              )}
            </Row>
            <Row className="center">
              <Button className="generate-button2 guardar-button" onClick={handleSave} loading={isSaving}>
                {t("rightColumn.saveProcess")}
              </Button>
            </Row>
            <Row className="center">
              <Button
                className="generate-button2 vistaprevia-button"
                onClick={toggleModal}
                disabled={!vertexResponse.finalDiagnosis || !isReadyToGenerate}
              >
                {t("rightColumn.previewReport")}
              </Button>
            </Row>
            <Row className="center separate">
              <Button className="button4 redesigned-danger-button" onClick={() => navigate(-1)}>
                {t("rightColumn.back")}
              </Button>
              <Popconfirm
                title={t("rightColumn.confirmNewReport")}
                description={t("rightColumn.loseData")}
                okText={t("rightColumn.yesContinue")}
                cancelText={t("rightColumn.cancel")}
                onConfirm={nuevoInforme}
                okButtonProps={{ danger: true }}
              >
                <Button className="button5 redesigned-secondary-button">{t("rightColumn.newReport")}</Button>
              </Popconfirm>
            </Row>
          </Col>
        </Row>
      )}
      <InformeModal
        isVisible={isModalVisible}
        onClose={() => {
          setIsModalVisibleInternal(false)
          setIsModalVisible(false)
        }}
        diagnostico={vertexResponse.finalDiagnosis || ""}
        solicitud={redaccion}
        observaciones={observations}
        images={[]}
        updateRightColumnState={updateStateFromModal}
      />
    </Col>
  )
}

export default RightColumn
